import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Clock, Loader2, Plus, Users, Bell, LogIn } from "lucide-react";
import { toast } from "sonner";
import { PageContainer, PageHeader, Card, Pill } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const emptyReservation = {
  guest_name: "",
  guest_phone: "",
  reservation_date: new Date().toISOString().slice(0, 10),
  reservation_time: "19:00",
  party_size: "2",
  restaurant_table_id: "",
  area_preference: "",
  notes: "",
};

const emptyWaitlist = {
  guest_name: "",
  guest_phone: "",
  party_size: "2",
  notes: "",
};

export default function ReservationsHub() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const counterId = user?.default_counter?.id;
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [resForm, setResForm] = useState(emptyReservation);
  const [waitForm, setWaitForm] = useState(emptyWaitlist);
  const [seatTableByWaitlist, setSeatTableByWaitlist] = useState({});

  const { data: tablesData } = useQuery({
    queryKey: ["reservation-tables", branchId],
    queryFn: async () => {
      const { data } = await api.get("/reservations/tables/list", { params: { branch_id: branchId } });
      return data.tables;
    },
    enabled: Boolean(branchId),
  });

  const { data: reservationsData, isLoading: resLoading } = useQuery({
    queryKey: ["reservations", branchId, date],
    queryFn: async () => {
      const { data } = await api.get("/reservations", { params: { branch_id: branchId, date } });
      return data;
    },
    enabled: Boolean(branchId),
  });

  const { data: waitlistData, isLoading: waitLoading } = useQuery({
    queryKey: ["waitlist", branchId],
    queryFn: async () => {
      const { data } = await api.get("/waitlist", { params: { branch_id: branchId } });
      return data.entries;
    },
    enabled: Boolean(branchId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["reservations", branchId] });
    queryClient.invalidateQueries({ queryKey: ["waitlist", branchId] });
    queryClient.invalidateQueries({ queryKey: ["table-floor", branchId] });
  };

  const createResMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/reservations", payload, { params: { branch_id: branchId } });
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setResForm(emptyReservation);
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not save reservation."),
  });

  const seatResMutation = useMutation({
    mutationFn: async ({ id, tableId }) => {
      const { data } = await api.post(
        `/reservations/${id}/seat`,
        { restaurant_table_id: tableId, counter_id: counterId },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not seat guest."),
  });

  const cancelResMutation = useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(`/reservations/${id}/cancel`, {}, { params: { branch_id: branchId } });
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not cancel."),
  });

  const addWaitMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/waitlist", payload, { params: { branch_id: branchId } });
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setWaitForm(emptyWaitlist);
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not add to waitlist."),
  });

  const notifyWaitMutation = useMutation({
    mutationFn: async ({ id, tableId }) => {
      const { data } = await api.post(
        `/waitlist/${id}/notify`,
        tableId ? { restaurant_table_id: Number(tableId) } : {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not notify guest."),
  });

  const seatWaitMutation = useMutation({
    mutationFn: async ({ id, tableId }) => {
      const { data } = await api.post(
        `/waitlist/${id}/seat`,
        { restaurant_table_id: Number(tableId), counter_id: counterId },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not seat guest."),
  });

  const leaveWaitMutation = useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(`/waitlist/${id}/leave`, {}, { params: { branch_id: branchId } });
      return data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not remove."),
  });

  const tables = tablesData ?? [];
  const reservations = reservationsData?.reservations ?? [];
  const waitlist = waitlistData ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Reservations & Waitlist"
        subtitle="Bookings, walk-in queue, WhatsApp table-ready pings, and seat-to-open table session"
      />

      <Tabs defaultValue="reservations">
        <TabsList className="mb-4">
          <TabsTrigger value="reservations">Reservations</TabsTrigger>
          <TabsTrigger value="waitlist">Waitlist ({waitlist.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="reservations">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Plus className="h-4 w-4" />
                New reservation
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Guest name</Label>
                  <Input value={resForm.guest_name} onChange={(e) => setResForm({ ...resForm, guest_name: e.target.value })} />
                </div>
                <div>
                  <Label>Phone (WhatsApp)</Label>
                  <Input value={resForm.guest_phone} onChange={(e) => setResForm({ ...resForm, guest_phone: e.target.value })} />
                </div>
                <div>
                  <Label>Party size</Label>
                  <Input type="number" value={resForm.party_size} onChange={(e) => setResForm({ ...resForm, party_size: e.target.value })} />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={resForm.reservation_date} onChange={(e) => setResForm({ ...resForm, reservation_date: e.target.value })} />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input type="time" value={resForm.reservation_time} onChange={(e) => setResForm({ ...resForm, reservation_time: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Table (optional)</Label>
                  <Select
                    value={resForm.restaurant_table_id || "none"}
                    onValueChange={(v) => setResForm({ ...resForm, restaurant_table_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Assign later" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {tables.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>{t.code} — {t.name} ({t.capacity}p)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={() =>
                  createResMutation.mutate({
                    ...resForm,
                    party_size: Number(resForm.party_size) || 2,
                    restaurant_table_id: resForm.restaurant_table_id ? Number(resForm.restaurant_table_id) : null,
                  })
                }
                disabled={createResMutation.isPending || !resForm.guest_name}
              >
                {createResMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
                Save & send confirmation
              </Button>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Bookings
                </h3>
                <Input type="date" className="w-36 h-8" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              {resLoading ? (
                <div className="text-muted-foreground flex gap-2 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : reservations.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6">No reservations for this date.</p>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-y-auto">
                  {reservations.map((r) => (
                    <div key={r.id} className="border rounded-md p-3 text-sm">
                      <div className="flex justify-between gap-2">
                        <div>
                          <div className="font-semibold">{r.guest_name}</div>
                          <div className="text-muted-foreground">{r.reservation_time} · {r.party_size} guests{r.table_code ? ` · T${r.table_code}` : ""}</div>
                          {r.guest_phone && <div className="text-xs text-muted-foreground">{r.guest_phone}</div>}
                        </div>
                        <Pill tone={r.status === "seated" ? "green" : r.status === "cancelled" ? "slate" : "blue"}>{r.status}</Pill>
                      </div>
                      {["pending", "confirmed"].includes(r.status) && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => seatResMutation.mutate({ id: r.id, tableId: r.restaurant_table_id })}
                            disabled={seatResMutation.isPending || !r.restaurant_table_id}
                          >
                            <LogIn className="h-3 w-3" /> Seat
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => cancelResMutation.mutate(r.id)} disabled={cancelResMutation.isPending}>
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="waitlist">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                Add to waitlist
              </h3>
              <div className="space-y-2">
                <div>
                  <Label>Guest name</Label>
                  <Input value={waitForm.guest_name} onChange={(e) => setWaitForm({ ...waitForm, guest_name: e.target.value })} />
                </div>
                <div>
                  <Label>Phone (for table-ready WhatsApp)</Label>
                  <Input value={waitForm.guest_phone} onChange={(e) => setWaitForm({ ...waitForm, guest_phone: e.target.value })} />
                </div>
                <div>
                  <Label>Party size</Label>
                  <Input type="number" value={waitForm.party_size} onChange={(e) => setWaitForm({ ...waitForm, party_size: e.target.value })} />
                </div>
              </div>
              <Button
                onClick={() => addWaitMutation.mutate({ ...waitForm, party_size: Number(waitForm.party_size) || 2 })}
                disabled={addWaitMutation.isPending || !waitForm.guest_name}
              >
                Add to queue
              </Button>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold mb-3">Queue</h3>
              {waitLoading ? (
                <div className="text-muted-foreground flex gap-2 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : waitlist.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6">Waitlist is empty.</p>
              ) : (
                <div className="space-y-2">
                  {waitlist.map((w) => (
                    <div key={w.id} className="border rounded-md p-3 text-sm">
                      <div className="flex justify-between">
                        <div>
                          <span className="font-bold text-violet-700">#{w.position}</span> {w.guest_name} · {w.party_size}p
                          {w.estimated_wait_minutes && (
                            <span className="text-muted-foreground ml-2">~{w.estimated_wait_minutes} min</span>
                          )}
                        </div>
                        <Pill tone={w.status === "notified" ? "amber" : "blue"}>{w.status}</Pill>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2 items-center">
                        <Select
                          value={seatTableByWaitlist[w.id] || ""}
                          onValueChange={(v) => setSeatTableByWaitlist((s) => ({ ...s, [w.id]: v }))}
                        >
                          <SelectTrigger className="h-8 w-32"><SelectValue placeholder="Table" /></SelectTrigger>
                          <SelectContent>
                            {tables.map((t) => (
                              <SelectItem key={t.id} value={String(t.id)}>{t.code}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            notifyWaitMutation.mutate({
                              id: w.id,
                              tableId: seatTableByWaitlist[w.id],
                            })
                          }
                          disabled={notifyWaitMutation.isPending}
                        >
                          <Bell className="h-3 w-3" /> Notify
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => seatWaitMutation.mutate({ id: w.id, tableId: seatTableByWaitlist[w.id] })}
                          disabled={seatWaitMutation.isPending || !seatTableByWaitlist[w.id]}
                        >
                          Seat
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => leaveWaitMutation.mutate(w.id)}>Left</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
